'use client';

import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Box } from '@chakra-ui/react';

import { useAppDispatch } from '@/store/hooks';
import { setUser, setIsAuthenticated } from '@/store/reducers/auth';
import { useGetUserMeQuery } from '@/store/rest/userApi';
import CookieService from '@/services/CookieService';
import { EUserTokenNames } from '@/types/user';
import Spinner from '@/components/ui/Spinner/Spinner';

const PUBLIC_PATHS = [
  '/login',
  '/set-password',
  '/reset-password',
] as const;

const DASHBOARD_PATH = '/';

const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const pathname = usePathname();
  const dispatch = useAppDispatch();

  const isPublicPath = PUBLIC_PATHS.some(path => pathname.startsWith(path));
  const hasValidToken = !!CookieService.getCookie(EUserTokenNames.accessToken);

  const { data: user, isLoading, error } = useGetUserMeQuery(undefined, {
    skip: isPublicPath || !hasValidToken,
  });

  useEffect(() => {
    if (isPublicPath && hasValidToken) {
      router.push(DASHBOARD_PATH);

      return;
    }

    if (!isPublicPath && !hasValidToken) {
      router.push('/login');

      return;
    }

    if (user && !isPublicPath) {
      dispatch(setUser(user));
      dispatch(setIsAuthenticated(true));
    }

    if (error && !isPublicPath) {
      router.push('/login');
    }
  }, [pathname, user, error, router, dispatch, isPublicPath, hasValidToken]);

  if (isPublicPath) {
    if (hasValidToken) {
      return null;
    }

    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" bg="background">
        <Spinner size={48} color="textPrimary" />
      </Box>
    );
  }

  if (!hasValidToken) {
    return null;
  }

  return <>{children}</>;
};

export default AuthGuard;
